<script lang="ts" setup>
import { computed, nextTick, onMounted, provide, ref, set, watch } from 'vue'
import { useLocalStorage, useTitle } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { $formatErrorMessage, $loadTextFromCDN, $log, $requestId } from '@/helpers'
import { STORAGE_AD_WELFARE, STORAGE_CUSTOM_THEME, STORAGE_DAILY_RED_PACK, STORAGE_HIDE_CHAPTER_READ_TIPS } from '@/constants'
import BizSwiperChapter from '@/components/BizSwiperChapter.vue'
import { api } from '@/api'
import { useConfigStore, useLoadingStore, useToastStore } from '@/stores'
import { event } from '@/event'
import { clamp, getToday } from '@/utils'
import { KS } from '@/kuaishou'
import { useLocalAdFree } from '@/composables'
import { track } from '@/track'

const props = defineProps<{
  contentId: string
  chapterId: string
}>()

const route = useRoute()
const router = useRouter()

provide('contentId', props.contentId)
provide('chapterId', props.chapterId)

const { $toast } = useToastStore()
const { $showLoading, $hideLoading } = useLoadingStore()
const configStore = useConfigStore()

const skeleton = ref(false)
const title = useTitle('加载中...')
const error = ref()
const book = ref<GetKuaishouContentGetcontentdetailResponse['data']>()
const chapter = ref<PostKuaishouReadPlayResponse['data']>()
const content = ref('')
const empty = computed(() => {
  return !content.value
})

// 单页停留时长
const stayPageSeconds = ref<Record<number, number | undefined>>({})

// Fetch Book
async function fetchBook() {
  const { data } = await api.get<any, GetKuaishouContentGetcontentdetailResponse>('/kuaishou/content/getContentDetail', { params: { contentId: props.contentId } })
  book.value = data
}
// Fetch latest read chapter & position
async function fetchLatestPosition() {
  const { data } = await api.post<any, PostKuaishouReadPosResponse>('/kuaishou/read/pos', { contentId: book.value!.id })
  router.replace({
    name: 'chapter',
    params: { contentId: book.value!.id, chapterId: data.contentChapterId },
    query: { ...route.query, swiperIndex: String(data.sectionMediaPlayedSeconds) },
  })
  return props.chapterId !== data.contentChapterId
}
async function fetchChapter() {
  try {
    const { data } = await api.post<any, PostKuaishouReadPlayResponse>('/kuaishou/read/play', {
      contentId: props.contentId,
      contentChapterId: props.chapterId,
      sequenceNo: 1,
    })
    chapter.value = data
    content.value = await $loadTextFromCDN(data.bookChapterFileUrl)
    title.value = chapter.value?.contentChapterName
    stayPageSeconds.value = {}
  }
  catch (error: any) {
    const { data, code } = error
    // Note: 特殊业务处理, 403 为未购买章节
    if (code === 403) {
      chapter.value = data
      content.value = await $loadTextFromCDN(data.bookChapterFileUrl)
      openModalLocked()
      return
    }
    console.error(error)
    $toast($formatErrorMessage(error))
  }
}

async function fetchTask() {
  try {
    const { data } = await api.get(`/kuaishou/ad/inspireNovelTask?novelId=${props.contentId}`)
    $log('Page: chapter', '#fetchTask', { data })
    // if (data.taskStatus === 'COMPLETE')
    // if (data.taskProcess === data.taskTarget)
    //   openModalCoin()
  }
  catch (error) {
    console.error(error)
  }
}

const firstLoad = ref(true)
onMounted(async () => {
  skeleton.value = true
  try {
    // Note: 先获取书籍信息, 书籍接口包含独家逻辑, 会矫正 `contentId` 和 `chapterId`
    await fetchBook()
    // Note: 如果章节不同, 会触发下面的 `watch` 避免重复请求
    const different = await fetchLatestPosition()
    if (different)
      return
    await fetchChapter()
  }
  finally {
    skeleton.value = false
  }
  swipeChapterFromQuery()
})
watch(() => props.chapterId, async (newVal: string, oldVal: string) => {
  skeleton.value = true
  chapter.value = undefined
  content.value = ''
  if (oldVal !== '0')
    firstLoad.value = false

  try {
    await fetchChapter()
    await fetchTask()
  }
  finally {
    skeleton.value = false
  }
  swipeChapterFromQuery()
})

// Swiper
const swiperIndex = ref(0)
const swiperChapterRef = ref()
const showNavbarTitle = computed(() => {
  return swiperIndex.value > 0
})
// Swipe to latest read position
function swipeChapterFromQuery() {
  const swiperIndex = Math.min(swiperChapterRef.value?.length ?? Number.POSITIVE_INFINITY, Number(route.query.swiperIndex ?? 0))
  nextTick(() => {
    changeChapterIndex(swiperIndex)
  })
}
function changeChapterIndex(index: number) {
  // 最新章节特殊处理
  if (index === -1) {
    swiperIndex.value = swiperChapterRef.value.length - 1
    return
  }

  // Note max = length - 1
  swiperIndex.value = Math.min(index, swiperChapterRef.value.length - 1)
}
const swipeInsertCount = ref(0)
const swipeBottomCount = ref(0)
const adBottomRef = ref()
const swipeQuickCount = ref(0)
const showInsertAdTimes = ref(0)
const showWelfareTimes = useLocalStorage(STORAGE_AD_WELFARE, { date: getToday(), times: 0 })
onMounted(() => {
  if (showWelfareTimes.value.date !== getToday())
    showWelfareTimes.value = { date: getToday(), times: 0 }
})
const { enabled: adFreeEnabled } = useLocalAdFree()
async function onChangeChapterIndex(index: number, pos: number) {
  swipeInsertCount.value += pos
  swipeBottomCount.value += pos
  $log('page:chapter', '#onChangeChapterIndex', { index, swipeInsertCount: swipeInsertCount.value, swipeBottomCount: swipeBottomCount.value, showInsertAdTimes: showInsertAdTimes.value, showWelfareTimes: showWelfareTimes.value.times })

  // Note: 连续非正常阅读提示
  if ((stayPageSeconds.value[swiperIndex.value - 1] ?? 0) < configStore.$state.pageReadTimeMin) {
    swipeQuickCount.value += 1
    if (swipeQuickCount.value >= configStore.$state.alertUnusualContinueTurnPage) {
      $toast(`连续快速翻页 ${swipeQuickCount.value} 次, 请正常阅读`, 3000)
      track('elementShow', { name: 'readCarefullyToast' })
      swipeQuickCount.value = 0
    }
  }
  else {
    swipeQuickCount.value = 0
  }

  $log('page:chapter', '#onChangeChapterIndex', { swipeQuickCount: swipeQuickCount.value, stay: stayPageSeconds.value[swiperIndex.value - 1] ?? 0 })

  // Note: 底部广告刷新
  if (swipeBottomCount.value === 5) {
    adBottomRef.value?.refresh()
    swipeBottomCount.value = 0
  }

  // Note: 满足条件, 开启插屏广告
  if (!adFreeEnabled.value && swipeInsertCount.value === Number(configStore.$state.displayIntervals)) {
    if (showWelfareTimes.value.times < configStore.$state.adPopupFuliDailyInspireMax
      && configStore.$state.adPopupDisplayFuliIntervalNum !== 0
      && showInsertAdTimes.value === configStore.$state.adPopupDisplayFuliIntervalNum
    ) {
      openWelfare()
      showWelfareTimes.value.times++
      showInsertAdTimes.value = 0
    }
    else {
      openAdFull()
      showInsertAdTimes.value++
    }
    swipeInsertCount.value = 0
  }

  reportSwiperIndex()
}
// Report swiper `index` position
async function reportSwiperIndex() {
  try {
    if (!swiperChapterRef.value)
      return
    await api.post<any, PostKuaishouReadReportResponse>('/kuaishou/read/report', {
      contentId: props.contentId,
      contentChapterId: props.chapterId,
      sectionMediaPlayedSeconds: swiperIndex.value ?? 0,
      videoDuration: swiperChapterRef.value?.length ?? 0,
    })
  }
  catch (error: any) {
    // TODO: Report Error
    console.error(error)
  }
}
async function onPrevChapter() {
  if (!chapter.value?.prev)
    return $toast('已经是第一章了~')
  await router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value.prev.contentChapterId }, query: { ...route.query, swiperIndex: '-1' } })
  onChangeChapterIndex(-1, -1)
}
async function onNextChapter() {
  if (!chapter.value?.next) {
    router.replace({ name: 'finished', params: { contentId: props.contentId }, query: route.query })
    return $toast('没有下一章了~')
  }
  await readTimeReport()

  await router.replace({ name: 'chapter', params: { contentId: props.contentId, chapterId: chapter.value.next.contentChapterId }, query: { ...route.query, swiperIndex: '0' } })
  onChangeChapterIndex(-1, 1)
}

// Note: 根据全局配置设置开始计时
const readTimeStart = ref(Date.now())
onMounted(() => {
  readTimeStart.value = Date.now()
  event.on('app:tick', (interval: number) => {
    set(stayPageSeconds.value, swiperIndex.value, (stayPageSeconds.value[swiperIndex.value] ?? 0) + interval / 1000)
  })
  KS.setPhysicalBack(() => {
    readTimeReport({ exit: true })
  })
})
const readTime = ref(0)
watch(() => stayPageSeconds.value, () => {
  let count = 0
  for (const [_, value] of Object.entries(stayPageSeconds.value)) {
    const single = clamp((value ?? 0), 0, configStore.$state.pageReadTimeMax)
    if (single < configStore.$state.pageReadTimeMin)
      continue
    count += single
  }

  if (count < configStore.$state.chapterReadTimeMin) {
    readTime.value = 0
    return
  }

  readTime.value = Math.round(count / configStore.$state.timeDilationCoefficient)
}, { deep: true })

async function readTimeReport({ exit = false } = {}) {
  $log('page:chapter', '#readTimeReport', { readTime: readTime.value })
  if (readTime.value === 0)
    return

  track('contentPlay', {
    contentId: props.contentId,
    chapterId: props.chapterId,
    readTime: Math.round(readTime.value),
  })

  $showLoading()
  try {
    // refs: https://docs.qingque.cn/d/home/eZQCayJ_04C4mUKeID6PSxPU2?identityId=21kSE82Rgsm#section=h.787eikgo8lg
    const { data } = await api.post('/kuaishou/ad/inspireNovelReport', {
      requestId: $requestId(),
      eventType: exit ? 'EXIT' : 'CHAPTER_END',
      novelId: props.contentId,
      chapter: chapter.value!.sequenceNo,
      chapterPercent: Math.round((swiperIndex.value + 1) / swiperChapterRef.value.length * 100),
      word: content.value.length ?? 0,
      startTime: readTimeStart.value,
      readTime: Math.round(readTime.value),
    })

    // toast : "30金币"
    const { toast } = data
    if (toast) {
      // get number from toast
      const coin = Number(toast.replace(/[^0-9]/ig, ''))
      openModalCoin(coin)
    }

    // Clear record
    stayPageSeconds.value = {}
    readTimeStart.value = Date.now()
  }
  catch (error: any) { console.error(error) }
  finally {
    $hideLoading()
  }
}

// Tips
const hideChapterReadTips = useLocalStorage(STORAGE_HIDE_CHAPTER_READ_TIPS, false)

// Popup: Theme
const theme = useLocalStorage(STORAGE_CUSTOM_THEME, {
  background: '#FFF',
  size: 'md' as 'sm' | 'md' | 'lg' | 'xl',
})
provide('theme', theme)
const popupTheme = ref(false)

// Popup: TOC
const popupTOC = ref(false)
watch(() => route.fullPath, () => {
  popupTOC.value = false
})

// Full: AD
const adFull = ref(false)
const modalWelfare = ref(false)
function openAdFull() {
  adFull.value = true
}
function openWelfare() {
  modalWelfare.value = true
}

// modal: 每日红包
const modalDailyRedPack = ref(false)
const localStorageDailyRedPack = useLocalStorage(STORAGE_DAILY_RED_PACK, '')
onMounted(() => {
  const today = getToday()
  if (localStorageDailyRedPack.value === today)
    return

  localStorageDailyRedPack.value = today
  modalDailyRedPack.value = true
})

// Modal: Coin
const modalCoin = ref(false)
const modalCoinNumber = ref(0)
const modalLocked = ref(false)
function openModalCoin(coin: number) {
  modalCoinNumber.value = coin
  modalCoin.value = true
}
function openModalLocked() {
  modalLocked.value = true
}
function onModalLockedClose() {
  // Note: simple reload
  if (!chapter.value?.prev) {
    window.location.reload()
    return
  }

  fetchLatestPosition()
  $toast('取消解锁, 返回上一章')
}
function onUnlock() {
  content.value = ''
  modalLocked.value = false
  fetchChapter()
}

function trackElementClickMenu() {
  track('elementClick', {
    name: 'readPageClick',
    contentId: props.contentId,
    chapterId: props.chapterId,
    button: 'menu',
  })
}
</script>

<template>
  <div class="pt-18 h-screen overflow-hidden" :style="{ background: theme.background }">
    <div v-if="skeleton">
      <div class="pt-20vh flex justify-center">
        <BasicSpinner class="mx-auto" />
      </div>
      <p class="mt-4 text-center font-bold">
        加载中...
      </p>
    </div>
    <div v-else-if="error">
      <BizBlockError :title="$formatErrorMessage(error)" />
    </div>
    <div v-else-if="empty">
      <BizBlockEmpty title="书籍章节内容不存在" />
    </div>
    <BizTrackShow
      v-else-if="book && chapter" class="h-full flex flex-col items-center"
      name="chapterPageShow" type="page"
      :extra="{
        contentId,
        chapterId,
        firstLoad,
        chapterNum: book.chapterNum,
        bookReadPercent: `${((chapter.sequenceNo / book.chapterNum) * 100).toFixed(2)}%`,
      }"
    >
      <BasicNavbar class="h-10" :title="showNavbarTitle && title" />
      <BizSwiperChapter
        v-if="content"
        ref="swiperChapterRef"
        v-model="swiperIndex"
        class="h-full pt-0 pb-24" :content="content"
        :title="chapter.contentChapterName" :progress="{ unit: 1 / book.chapterNum, index: chapter.sequenceNo }"
        @menu="() => { popupTheme = true; trackElementClickMenu() }" @change="onChangeChapterIndex"
        @prevChapter="onPrevChapter" @nextChapter="onNextChapter"
      />
      <BizAdBottom ref="adBottomRef" :content-id="contentId" :chapter-id="chapterId" />

      <BizModalDailyRedPack v-if="modalDailyRedPack" v-model="modalDailyRedPack" />

      <BizAdFull v-if="adFull" v-model="adFull" :content-id="contentId" :chapter-id="chapterId" />
      <BizModalWelfare v-if="modalWelfare" v-model="modalWelfare" />

      <!-- Tips -->
      <div v-if="!hideChapterReadTips" class="fixed inset-0 bg-#000000cc z-100" @click="hideChapterReadTips = true">
        <img class="w-full px-10 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" src="@/assets/cover-read-tips.png" alt="图片:阅读提示">
        <div class="h-screen w-1/3 absolute top-0 left-1/3" style="background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, rgba(255, 255, 255, 0.10) 48.96%, rgba(255, 255, 255, 0.00) 100%);" />
      </div>

      <!-- Popup: 设置主题 -->
      <BizPopupTheme
        v-model="popupTheme"
        :prev="!!chapter?.prev" :next="!!chapter?.next"
        :content-id="contentId" :chapter-id="chapterId"
        @changeSize="size => theme.size = size"
        @changeBackground="background => theme.background = background" @toc="popupTOC = true"
        @chapterPrev="onPrevChapter"
        @chapterNext="onNextChapter"
      />
      <!-- Popup: 书籍目录 -->
      <BizPopupBookTOC v-model="popupTOC" :book="book" :content-id="contentId" :chapter-id="chapterId" :chapter-no="chapter.sequenceNo" />

      <!-- Modal -->
      <BizModalCoin v-if="modalCoin" v-model="modalCoin" :coin="modalCoinNumber" />
      <BizModalLocked
        v-if="modalLocked" v-model="modalLocked"
        :content-id="contentId" :chapter-id="chapterId"
        @close="onModalLockedClose" @cancel="onModalLockedClose" @unlock="onUnlock"
      />
    </BizTrackShow>
  </div>
</template>
